import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { KpiCard } from "./KpiCard";
import { TableSkeleton } from "./TableSkeleton";
import { tokens } from "../theme";
import { foyerApi } from "../http/foyer";
import type {
  MeteredFleetMonth,
  MeteredMonthlyReport,
  MeteredPremiseMonthWire,
} from "@shared/meteredMonthly";

/**
 * The monthly metered-usage KPI (2026-09-23): what the reach department is
 * measured on. Per premise per month: did its signage reach its own target
 * (binary, against the pass line), and per month: what share of metered
 * premises passed, against the fleet goal. Both lines are set on General
 * settings. Closed months are frozen server-side; the running month is
 * provisional and says so.
 */

const pctFmt = (n: number | null): string =>
  n === null
    ? "-"
    : `${n.toLocaleString("sv-SE", { maximumFractionDigits: 0 })}%`;
const hFmt = (n: number): string =>
  `${n.toLocaleString("sv-SE", { maximumFractionDigits: 1 })}h`;

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function cellColors(r: MeteredPremiseMonthWire): { fg: string; bg: string } {
  if (r.inProgress) return { fg: tokens.inkMuted, bg: "transparent" };
  return r.passed
    ? { fg: tokens.success, bg: tokens.successTint }
    : { fg: tokens.error, bg: tokens.errorTint };
}

function PremiseMonthCell({ r }: { r: MeteredPremiseMonthWire }) {
  const c = cellColors(r);
  const target =
    r.mode === "hours"
      ? `${hFmt(r.configuredValue)}/month`
      : `${r.configuredValue}% of open hours`;
  const detail = [
    `Delivered ${hFmt(r.actualHours)} of ${hFmt(r.targetHours)} owed (${pctFmt(
      r.attainmentPercent
    )} of target).`,
    `Uptime ${pctFmt(r.uptimePercent)} of ${hFmt(r.openHours)} open.`,
    `Target: ${target}${r.mixed ? " (changed during the month)" : ""}.`,
    r.prorated ? "Prorated: metered for only part of the month." : null,
    r.noOpeningHours
      ? "No opening hours set - nothing counts until they are."
      : null,
    r.inProgress
      ? "Month in progress - figures so far against the full month's target."
      : r.frozen
        ? `Frozen at month close (pass line ${r.passThresholdPercent}%).`
        : "Closed, not frozen yet - computed live until the close-out runs.",
    r.screens.length > 1
      ? `${r.screens.length} screens, pooled: ${r.screens
          .map(
            (s) => `${s.roomId ?? s.screenId} ${pctFmt(s.attainmentPercent)}`
          )
          .join(", ")}.`
      : null,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{detail}</span>}>
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "flex-end",
          px: 1,
          py: 0.25,
          borderRadius: "6px",
          bgcolor: c.bg,
          color: c.fg,
          minWidth: 64,
        }}
        data-testid={`metered-cell-${r.monthKey}`}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "inherit" }}>
          {pctFmt(r.attainmentPercent)}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "inherit" }}>
          {r.inProgress ? "so far" : r.passed ? "Pass" : "Fail"}
          {r.prorated ? " · prorated" : ""}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function FleetMonthCell({ f }: { f: MeteredFleetMonth }) {
  const color =
    f.goalMet === null || f.inProgress
      ? tokens.inkMuted
      : f.goalMet
        ? tokens.success
        : tokens.error;
  return (
    <Box sx={{ textAlign: "right", color }} data-testid={`fleet-${f.monthKey}`}>
      <Typography sx={{ fontWeight: 700, color: "inherit" }}>
        {pctFmt(f.passRatePercent)}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "inherit" }}>
        {f.counted > 0 ? `${f.passed}/${f.counted} passed` : "none metered"}
        {f.inProgress ? " · so far" : ""}
      </Typography>
    </Box>
  );
}

/** The report as CSV - one row per premise per month, for KPI reporting
 *  outside the CMS. */
export function meteredMonthlyCsv(report: MeteredMonthlyReport): string {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const round = (n: number | null) =>
    n === null ? "" : String(Math.round(n * 10) / 10);
  const header = [
    "premise_id",
    "premise",
    "month",
    "mode",
    "configured",
    "target_hours",
    "actual_hours",
    "open_hours",
    "uptime_percent",
    "attainment_percent",
    "passed",
    "pass_line_percent",
    "prorated",
    "in_progress",
    "frozen",
  ];
  const lines = [header.join(",")];
  for (const p of report.premises) {
    for (const m of report.months) {
      const r = p.months[m];
      if (!r) continue;
      lines.push(
        [
          p.premiseId,
          p.premiseName,
          m,
          r.mode,
          r.configuredValue,
          round(r.targetHours),
          round(r.actualHours),
          round(r.openHours),
          round(r.uptimePercent),
          round(r.attainmentPercent),
          r.inProgress ? "" : r.passed ? "yes" : "no",
          r.passThresholdPercent,
          r.prorated ? "yes" : "no",
          r.inProgress ? "yes" : "no",
          r.frozen ? "yes" : "no",
        ]
          .map(esc)
          .join(",")
      );
    }
  }
  return lines.join("\n");
}

function downloadCsv(report: MeteredMonthlyReport) {
  const blob = new Blob([meteredMonthlyCsv(report)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `metered-usage-${report.months[0] ?? ""}_${
    report.months[report.months.length - 1] ?? ""
  }.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MeteredMonthlyView() {
  const [report, setReport] = useState<MeteredMonthlyReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    foyerApi
      .fleetMeteredUsageMonthly({ months: 12 })
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <Typography sx={{ color: tokens.error }}>{error}</Typography>;
  }
  if (report === null) return <TableSkeleton rows={5} />;

  const closed = report.fleet.filter((f) => !f.inProgress && f.counted > 0);
  const lastClosed = closed[closed.length - 1];
  const running = report.fleet.find((f) => f.inProgress);

  return (
    <Box data-testid="metered-monthly">
      <Stack
        direction="row"
        sx={{ gap: 3, flexWrap: "wrap", mb: 3, alignItems: "stretch" }}
      >
        <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
          <KpiCard
            label={
              lastClosed
                ? `Premises passing - ${monthLabel(lastClosed.monthKey)}`
                : "Premises passing - last closed month"
            }
            value={lastClosed ? pctFmt(lastClosed.passRatePercent) : "-"}
            sub={
              lastClosed
                ? `${lastClosed.passed} of ${lastClosed.counted} · goal ${report.goalPercent}% · ${
                    lastClosed.goalMet ? "met" : "missed"
                  }`
                : "No closed month with data yet"
            }
            icon={<FlagOutlinedIcon />}
            tint={
              lastClosed?.goalMet === false
                ? tokens.errorTint
                : tokens.successTint
            }
            accent={
              lastClosed?.goalMet === false ? tokens.error : tokens.success
            }
          />
        </Box>
        <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
          <KpiCard
            label={
              running
                ? `${monthLabel(running.monthKey)} so far`
                : "This month so far"
            }
            value={running ? pctFmt(running.passRatePercent) : "-"}
            sub={
              running
                ? `${running.passed} of ${running.counted} already at target · provisional`
                : "No metered premises this month"
            }
            icon={<InsightsOutlinedIcon />}
            tint={tokens.surface}
            accent={tokens.inkMuted}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}
      >
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          A premise passes a month at {report.passThresholdPercent}% of its
          target or more - binary, so anything less counts as 0. Hover a cell
          for the hours behind it.
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => downloadCsv(report)}
          disabled={report.premises.length === 0}
          data-testid="metered-monthly-csv"
        >
          CSV
        </Button>
      </Stack>

      {report.premises.length === 0 ? (
        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "12px",
            p: 5,
            textAlign: "center",
            bgcolor: tokens.surface,
          }}
          data-testid="metered-monthly-empty"
        >
          <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
            No metered months yet
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{ border: `1px solid ${tokens.divider}`, borderRadius: "12px" }}
        >
          <Table size="small" data-testid="metered-monthly-table">
            <TableHead>
              <TableRow>
                <TableCell>Premise</TableCell>
                {report.months.map((m) => (
                  <TableCell key={m} align="right">
                    {monthLabel(m)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: tokens.surface }}>
                <TableCell>
                  <Typography sx={{ fontWeight: 700 }}>Fleet</Typography>
                  <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>
                    Share passing · goal {report.goalPercent}%
                  </Typography>
                </TableCell>
                {report.fleet.map((f) => (
                  <TableCell key={f.monthKey} align="right">
                    <FleetMonthCell f={f} />
                  </TableCell>
                ))}
              </TableRow>
              {report.premises.map((p) => (
                <TableRow key={p.premiseId} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>
                      {p.premiseName}
                    </Typography>
                  </TableCell>
                  {report.months.map((m) => (
                    <TableCell key={m} align="right">
                      {p.months[m] ? (
                        <PremiseMonthCell r={p.months[m]!} />
                      ) : (
                        <Typography
                          sx={{ fontSize: 12, color: tokens.inkMuted }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
